import React from "react";
import handleClassNames from "./handleClassNames";
/* la fonction isInStructure permet de déterminer si une structure de blocs
 * donnée contient ou non un bloc.
 * les paramètres sont les suivants :
 * structure : la structure de blocs à analyser
 * recursive : la structure doit-elle être analysée de façon récursive, en parcourant les blocs enfants ?
 * block : le bloc recherché. On peut choisir un nom ou des attributs
 */
const isInStructure = (options) => {
    const { structure, recursive, block } = options;
    const { name, attributes } = block;
    return structure.some((e) => {
        if (e === null) {
            return false;
        }
        const { innerBlocks } = e;
        if (recursive &&
            innerBlocks &&
            innerBlocks.length &&
            isInStructure({ ...options, structure: innerBlocks })) {
            return true;
        }
        if (name !== e.name) {
            return false;
        }
        if (attributes &&
            Object.entries(attributes).some(([attribute, value]) => {
                if (typeof value === "function") {
                    return !value(e.attributes[attribute]);
                }
                return e.attributes[attribute] !== value;
            })) {
            return false;
        }
        return true;
    });
};
/* structure modification utilities */
const enhanceColumns = (block, parent = null) => {
    if (block === null || typeof block === "undefined") {
        return null;
    }
    const typedBlock = block;
    if (!typedBlock.name) {
        return block;
    }
    const outputBlock = { ...typedBlock };
    /* work recursively */
    if (typedBlock.innerBlocks && typedBlock.innerBlocks.length) {
        outputBlock.innerBlocks = typedBlock.innerBlocks.map((e) => enhanceColumns(e, typedBlock));
    }
    /* only work on columns */
    if (typedBlock.name !== "core/columns") {
        return outputBlock;
    }
    /* add a hasImage attribute if a core/image block is found in structure of columns */
    if (isInStructure({
        structure: typedBlock.innerBlocks,
        block: {
            name: "core/image",
        },
        recursive: true,
    })) {
        outputBlock.attributes = { ...outputBlock.attributes, hasImage: true };
    }
    /* only work blocks with normal content length
     * (eg. not wide-width nor full-width)
     * and top-level blocks
     * */
    if (typedBlock.attributes.alignfull === true || parent !== null) {
        return outputBlock;
    }
    /* consider only columns w/ inner H2 */
    if (isInStructure({
        structure: typedBlock.innerBlocks,
        block: {
            name: "core/heading",
            attributes: {
                level: (attr) => typeof attr === "undefined" || attr === "h2",
            },
        },
        recursive: true,
    })) {
        outputBlock.attributes = { ...outputBlock.attributes, isSection: true };
    }
    return outputBlock;
};
/*
const handleSpacer = (block, index, structure) => {
  if (block === null || typeof block === "undefined") {
    return null;
  }

  if (!block.name) {
    return block;
  }
  // work recursively
  if (block.innerBlocks && block.innerBlocks.length) {
    return {...block, innerBlocks: block.innerBlocks.map(e => handleSpacer(e, index, structure)) };
  }
  // only work with spacer
  if (block.name !== "core/spacer") {
    return block;
  }
  // if spacer has neighbour (this could be better)
  // then this neighbour inherits the spacer's attributes

  if (structure[index+1]) {
    structure[index+1].attributes = {
      ...(structure[index+1].attributes || {}),
      spacer: block.attributes
    };
  }
  // nullify block so it's ignored by Parser
  return null;
};*/
const handleContent = (block, children, blockMap) => {
    if (block === null || typeof block === "undefined") {
        return null;
    }
    if (!block.name && !Object.values(blockMap).includes(block.type.name)) {
        return block;
    }
    /* work recursively */
    if (block.name !== "core/query" &&
        block.name !== "core/post-template" &&
        block.name !== "core/query-pagination" &&
        block.innerBlocks &&
        block.innerBlocks.length) {
        return {
            ...block,
            innerBlocks: block.innerBlocks.map((e) => handleContent(e, children, blockMap)),
        };
    }
    /* only work with dynamic content */
    if (!children ||
        (block.name && !blockMap[block.name]) ||
        (block.type &&
            block.type._ges_name &&
            !Object.values(blockMap).includes(block.type._ges_name))) {
        return block;
    }
    /* substitute content block by pre-parser's children */
    const haystack = typeof children?.find === "undefined" ? [children] : children;
    const component = haystack.find((e) => {
        if (!e || !e.type._ges_name) {
            return false;
        }
        if (block.name) {
            return e.type._ges_name === blockMap[block.name];
        }
        return e.type._ges_name === block.type._ges_name;
    });
    if (!component) {
        return block;
    }
    /* merge props */
    const newProps = {
        ...component.props,
        attributes: {
            ...(block.attributes || {}),
            ...(block.props?.attributes || {}),
            ...(component.attributes || {}),
            ...(component.props?.attributes || {}),
        },
        innerBlocks: [
            ...(block.innerBlocks || []),
            ...(block.props?.innerBlocks || []),
            ...(component.innerBlocks || []),
            ...(component.props?.innerBlocks || []),
        ],
    };
    if (typeof window === "undefined" &&
        JSON.stringify(component.props) === JSON.stringify(newProps)) {
        return block;
    }
    return React.cloneElement(component, newProps);
};
const removeLockedGroups = (block) => {
    if (block === null || typeof block === "undefined") {
        return null;
    }
    if (!block.name) {
        return block;
    }
    block = block;
    /* remove "locked-group-" from block name */
    block.name = block.name.replace("gco/locked-group-", "gco/");
    /* work recursively */
    if (block.innerBlocks && block.innerBlocks.length) {
        return {
            ...block,
            innerBlocks: block.innerBlocks.map((e) => removeLockedGroups(e)),
        };
    }
    return block;
};
const getStructure = ({ children, breadcrumbs, customBreadcrumbs, ...props }) => {
    //~ let [structure, setStructure] = useState([]);
    const updateStructure = (oldStructure) => {
        let newStructure = typeof oldStructure === "string"
            ? JSON.parse(oldStructure)
            : oldStructure;
        if (!newStructure?.length) {
            return [];
        }
        /** reorganize structure **/
        //handle custom breadcrumbs position
        if (customBreadcrumbs && typeof oldStructure === "string") {
            newStructure = [newStructure[0], breadcrumbs, ...newStructure.slice(1)];
        }
        //handle context-dependant attributes & blocks
        const blockMap = {
            "gco/content": "Content",
            "core/query": "Query",
            "core/post-template": "PostTemplate",
            "core/query-pagination": "QueryPagination",
            "core/post-title": "PostTitle",
            "core/post-terms": "PostTerms",
            "core/post-excerpt": "PostExcerpt",
            "core/post-featured-image": "PostFeaturedImage",
            "core/post-date": "PostDate",
            "core/term-description": "TermDescription",
        };
        newStructure = newStructure.map((block /*, index, array*/) => {
            block = removeLockedGroups(block);
            block = enhanceColumns(block);
            //~ /* block = handleSpacer(block, index, array); */
            block = handleClassNames(block);
            block = handleContent(block, children, blockMap);
            return block;
        });
        return newStructure;
    };
    return updateStructure(props.structure);
};
export default getStructure;
